/**
 * Share Validation Schemas
 *
 * Zod schemas for validating resource sharing (plans & workouts). A share
 * request targets one or more recipients identified by username (not email —
 * usernames are the recipient key, mirroring the friend/invite flow) plus an
 * optional note. A plan share may additionally carry a schedule — the dates the
 * recipients are being asked to train on.
 */

import { planScheduleMapSchema } from './plan-schedule.schemas';
import { z } from './zod';

/**
 * How many people one share may reach.
 *
 * Exported because three places need it and had three different answers: this
 * schema said 50, `ShareModal` capped its picker at 20, and the API rejects at
 * 10. The API's number is the real one.
 */
export const SHARE_RECIPIENT_LIMIT = 10;

/**
 * A single recipient username. Permissive on purpose (the backend owns the
 * canonical username rules) — just a non-empty, bounded, trimmed string.
 */
export const shareUsernameSchema = z
  .string()
  .trim()
  .min(1, 'A recipient username is required')
  .max(255, 'Username is too long');

/** The resource kinds that can be shared. */
export const shareResourceTypeSchema = z.enum(['plan', 'workout']);

/**
 * Full share request payload. `share_with` is a non-empty list of usernames.
 */
export const shareResourceConfigSchema = z.object({
  share_with: z
    .array(shareUsernameSchema)
    .min(1, 'Add at least one person to share with')
    .max(
      SHARE_RECIPIENT_LIMIT,
      `You can share with up to ${SHARE_RECIPIENT_LIMIT} people at once`
    ),
  share_id: z.string().min(1, 'Share ID is required'),
  share_type: shareResourceTypeSchema,
  note: z.string().max(500, 'Note is too long'),
  include_notes: z.boolean(),
  /**
   * Dates to workout hashids — the same shape `PATCH /plan/{plan}/schedule`
   * takes, and reusing its schema so the two cannot drift.
   *
   * A map rather than a list of `{ id, date }` pairs, which is what this was.
   * The date is the unique thing: keying by workout id let one workout hold
   * only one date, so a plan repeating three workouts across eight weeks was
   * shared as three sessions rather than twenty-four. Keying by date also
   * enforces one session per day for free, which is the rule the recipient's
   * own schedule endpoint will hold them to once they start editing it.
   *
   * Omitted entirely for an unscheduled share; a workout share has no use for
   * it at all.
   */
  schedule: planScheduleMapSchema.optional(),
});

export type ShareResourceConfigInput = z.input<
  typeof shareResourceConfigSchema
>;
export type ShareResourceConfigOutput = z.output<
  typeof shareResourceConfigSchema
>;

/**
 * Validate a share configuration. Throws a {@link z.ZodError} on failure;
 * service callers wrap it via `invalidDataErrorFromZod` for a user-facing
 * message.
 *
 * @param data - The share configuration to validate
 * @returns The parsed configuration
 */
export function validateShareResourceConfig(
  data: unknown
): ShareResourceConfigOutput {
  return shareResourceConfigSchema.parse(data);
}
